import { DATE_ORDER_MESSAGE, type DateRange } from '@/components/ui/FilterBar';

/**
 * The dashboard's date range: what the address says, what the control offers,
 * and the span a change is measured against.
 *
 * Days are `YYYY-MM-DD` strings throughout, the same shape `FilterBar` and a
 * date input use. Arithmetic is done on them at midnight UTC, so a clock
 * change in the viewer's time zone can never make a day 23 or 25 hours long
 * and shift a range by one.
 */

export type Preset = '7d' | '30d' | '90d' | 'custom';

/** A range the page can show: which option it came from, and its two days. */
export interface ResolvedRange extends DateRange {
  preset: Preset;
}

/** The fixed spans, in the order the control lists them. */
export const PRESETS = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
] as const satisfies readonly { value: Preset; label: string; days: number }[];

/** The wireframe's default, and what any address nobody could have chosen falls back to. */
export const DEFAULT_PRESET = '30d' satisfies Preset;

export const CUSTOM_LABEL = 'Custom';

export const FUTURE_DATE_MESSAGE = 'Choose a date that is not in the future.';

/**
 * The longest custom range the backend answers, counting both ends, as its
 * `resolve_window` counts them: 1 January to 1 January of the next year is
 * 366 days and allowed. Refused here so its 422 is never the first thing
 * somebody sees.
 */
export const MAX_SPAN_DAYS = 366;
export const MAX_SPAN_MESSAGE = `Choose a range of at most ${String(MAX_SPAN_DAYS)} days.`;
export const MISSING_FROM_MESSAGE = 'Enter a From date.';
export const MISSING_TO_MESSAGE = 'Enter a To date.';

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Today in the viewer's own calendar, which is the day they think it is. */
export function todayIso(now: Date = new Date()): string {
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** True for a real calendar day. "2026-02-30" has the right shape and is not one. */
export function isIsoDay(value: string | null | undefined): value is string {
  if (!value || !DAY_PATTERN.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/** The day `days` after `day`; negative goes back. */
export function addDays(day: string, days: number): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  return new Date(parsed.getTime() + days * DAY_MS).toISOString().slice(0, 10);
}

/** How many days a range covers, counting both ends. */
export function dayCount(range: DateRange): number {
  const from = new Date(`${range.from}T00:00:00Z`).getTime();
  const to = new Date(`${range.to}T00:00:00Z`).getTime();
  return Math.round((to - from) / DAY_MS) + 1;
}

/** Every day in a range, first to last. */
export function eachDay(range: DateRange): string[] {
  return Array.from({ length: dayCount(range) }, (_, index) =>
    addDays(range.from, index),
  );
}

/** A fixed span ending today, as concrete dates. */
export function presetRange(
  preset: Exclude<Preset, 'custom'>,
  today: string,
): ResolvedRange {
  const days = PRESETS.find((option) => option.value === preset)!.days;
  return { preset, from: addDays(today, -(days - 1)), to: today };
}

function isFixedPreset(value: string | null): value is Exclude<Preset, 'custom'> {
  return PRESETS.some((option) => option.value === value);
}

/** Why a custom range cannot be used, and which of the two fields is at fault. */
export interface RangeProblem {
  field: 'from' | 'to';
  message: string;
}

/**
 * Nothing when a custom range can be shown, otherwise the reason and the field
 * it belongs to. Checked in the order somebody fills the fields in: a blank
 * field first, then a day in the future, then the two the wrong way round -
 * which is said against To, as `FilterBar` says it.
 */
export function validateCustom(
  from: string,
  to: string,
  today: string,
): RangeProblem | null {
  if (!isIsoDay(from)) return { field: 'from', message: MISSING_FROM_MESSAGE };
  if (!isIsoDay(to)) return { field: 'to', message: MISSING_TO_MESSAGE };
  if (from > today) return { field: 'from', message: FUTURE_DATE_MESSAGE };
  if (to > today) return { field: 'to', message: FUTURE_DATE_MESSAGE };
  if (to < from) return { field: 'to', message: DATE_ORDER_MESSAGE };
  if (dayCount({ from, to }) > MAX_SPAN_DAYS)
    return { field: 'to', message: MAX_SPAN_MESSAGE };
  return null;
}

/**
 * The range an address asks for.
 *
 * Anything that does not make sense - an unknown preset, a day that does not
 * exist, only one end, a range running backwards or into the future - is
 * Last 30 days rather than an error, so an old bookmark or a hand-typed address
 * never breaks the page.
 */
export function parseRange(searchParams: URLSearchParams, today: string): ResolvedRange {
  const preset = searchParams.get('range');
  if (isFixedPreset(preset)) return presetRange(preset, today);

  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  if (preset === null && validateCustom(from, to, today) === null) {
    return { preset: 'custom', from, to };
  }

  return presetRange(DEFAULT_PRESET, today);
}

/**
 * The address form of a range. A preset is written by name, not as dates, so
 * "Last 7 days" bookmarked today still means the last seven days next week.
 */
export function toSearchParams(range: ResolvedRange): URLSearchParams {
  if (range.preset === 'custom') {
    return new URLSearchParams({ from: range.from, to: range.to });
  }
  return new URLSearchParams({ range: range.preset });
}

/** The span of the same length ending the day before the range begins. */
export function previousPeriod(range: DateRange): DateRange {
  const to = addDays(range.from, -1);
  return { from: addDays(to, -(dayCount(range) - 1)), to };
}

/**
 * A day as people say it, in the browser's locale: "6 September" or
 * "September 6". Read at UTC, the way the day was written, so it is never the
 * day before in a time zone west of Greenwich.
 */
export function formatDay(day: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}
